import React, { useEffect, useState } from "react";
import EmployeeNumberConfigSection from "./EmployeeNumberConfigSection";
import TagEditorPage from "./TagEditorPage";
import DropdownManager from "./DropdownManager";
import InterviewManager from "./InterviewManager";
import TrainingQuiz from "./TrainingQuiz";
import CustomTaskCreateHelper from "./CustomTaskCreateHelper";
import ReferenceCheckAdminPage from "./ReferenceCheckAdminPage";
import NotificationAlertsPage from "./NotificationAlertsPage";
import ApiTokensPage from "./ApiTokensPage";
import AnnouncementCenter from "./AnnouncementCenter";
import SchedulerManagementPage from "./SchedulerManagementPage";
import AdminPageHeader from "../../components/AdminPageHeader";
import useAccess from "../../hooks/useAccess";
import "../../styles/AdministrationPages.css";
import "../../styles/AccountSettingsPage.css";

const TABS = [
  { key: "profile", label: "Profile", resource: "Profile", icon: "fas fa-id-card" },
  { key: "tags", label: "Tags", resource: "Tags", icon: "fas fa-tags" },
  {
    key: "dropdownManager",
    label: "Dropdown Manager",
    resource: "Dropdown Manager",
    icon: "fas fa-list",
  },
  {
    key: "interviewQuestion",
    label: "Interview Question",
    resource: "Interview Question",
    icon: "fas fa-comments",
  },
  {
    key: "trainingQuiz",
    label: "Training Quiz",
    resource: "Training Quiz",
    icon: "fas fa-graduation-cap",
  },
  {
    key: "customTaskCreateHelper",
    label: "Custom Task Create Helper",
    resource: "Custom Task Create Helper",
    icon: "fas fa-clipboard-list",
  },
  {
    key: "referenceCheck",
    label: "Reference Check",
    resource: "Reference Check",
    icon: "fas fa-user-check",
  },
  {
    key: "notificationsAlerts",
    label: "Notifications / Alerts",
    resource: "Notifications / Alerts",
    icon: "fas fa-bell",
  },
  {
    key: "announcementCenter",
    label: "Announcement Center",
    resource: "Announcement Center",
    icon: "fas fa-bullhorn",
  },
  {
    key: "schedulerManagement",
    label: "Scheduler Management",
    resource: "Scheduler Management",
    icon: "fas fa-clock",
  },
  {
    key: "apiTokens",
    label: "API Tokens",
    resource: "API Tokens",
    icon: "fas fa-key",
  },
];

const AccountSettingsPage = () => {
  const { hasPermission } = useAccess();
  const [tab, setTab] = useState(
    () => localStorage.getItem("accountSettingsTab") || "profile",
  );

  const isAppAdmin = Number(localStorage.getItem("userRole")) === 1;
  let isSuperAdmin = false;
  try {
    const currentUser = JSON.parse(localStorage.getItem("me") || "{}");
    const roleIds = typeof currentUser.emp_roles === "string"
      ? JSON.parse(currentUser.emp_roles || "[]")
      : currentUser.emp_roles;
    isSuperAdmin = Array.isArray(roleIds) && roleIds.map(Number).includes(17);
  } catch {
    isSuperAdmin = false;
  }
  const visibleTabs = TABS.filter((tabItem) =>
    (tabItem.key === "announcementCenter" && isAppAdmin)
      || (tabItem.key === "schedulerManagement" && isSuperAdmin)
      || hasPermission("AccountSetting", tabItem.resource, "view"),
  );

  useEffect(() => {
    if (!visibleTabs.some((tabItem) => tabItem.key === tab)) {
      const fallbackTab = visibleTabs[0]?.key || "profile";
      setTab(fallbackTab);
      localStorage.setItem("accountSettingsTab", fallbackTab);
    }
  }, [tab, visibleTabs]);

  const selectTab = (tabKey) => {
    setTab(tabKey);
    localStorage.setItem("accountSettingsTab", tabKey);
  };

  return (
    <div className="admin-standard-page account-settings-page">
      <AdminPageHeader
        icon="fas fa-cog"
        title="Admin Settings"
        description="Manage system configuration, reusable task tools, and administrator settings."
      />

      <section className="admin-standard-workspace account-settings-workspace">
        <div className="account-settings-layout">
          <aside className="account-settings-sidebar">
            <div className="account-settings-sidebar-title">
              <i className="fas fa-sliders-h" aria-hidden="true" />
              <span>Administration</span>
            </div>
            <nav role="tablist" aria-label="Account settings sections">
              {visibleTabs.map((tabItem) => (
                <button
                  key={tabItem.key}
                  type="button"
                  data-no-standard-button="true"
                  role="tab"
                  aria-selected={tab === tabItem.key}
                  className={tab === tabItem.key ? "active" : ""}
                  onClick={() => selectTab(tabItem.key)}
                >
                  <i className={tabItem.icon} aria-hidden="true" />
                  <span>{tabItem.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          <div className="admin-standard-workspace-body account-settings-content">
            {tab === "profile" && <EmployeeNumberConfigSection />}
            {tab === "tags" && <TagEditorPage hideAssignTab={true} />}
            {tab === "dropdownManager" && <DropdownManager />}
            {tab === "interviewQuestion" && <InterviewManager />}
            {tab === "trainingQuiz" && <TrainingQuiz />}
            {tab === "customTaskCreateHelper" && <CustomTaskCreateHelper />}
            {tab === "referenceCheck" && <ReferenceCheckAdminPage />}
            {tab === "notificationsAlerts" && <NotificationAlertsPage />}
            {tab === "announcementCenter" && <AnnouncementCenter />}
            {tab === "schedulerManagement" && <SchedulerManagementPage />}
            {tab === "apiTokens" && <ApiTokensPage />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountSettingsPage;
